import type { LandKind, MapTerrain } from "./types.js";

/** True when `(x,z)` lies on the heightfield, including its outer edge. */
export function terrainContains(t: MapTerrain, x: number, z: number): boolean {
  return (
    x >= t.origin.x &&
    z >= t.origin.z &&
    x <= t.origin.x + (t.width - 1) * t.cellSize &&
    z <= t.origin.z + (t.depth - 1) * t.cellSize
  );
}

/**
 * True when quad `(i, j)` has been cut out of the heightfield
 * (`MapTerrain.holes`). Once per capsule per tick, so a binary search over the
 * sorted array, no Set.
 */
export function terrainQuadIsHole(t: MapTerrain, i: number, j: number): boolean {
  const holes = t.holes;
  if (holes === undefined || holes.length === 0) return false;
  const want = j * (t.width - 1) + i;
  let lo = 0;
  let hi = holes.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = holes[mid] as number;
    if (v === want) return true;
    if (v < want) lo = mid + 1;
    else hi = mid - 1;
  }
  return false;
}

/**
 * Height on the same two triangles the renderer uses, or `null` outside — and
 * `null` over a hole quad.
 */
export function terrainHeightAt(t: MapTerrain, x: number, z: number): number | null {
  if (!terrainContains(t, x, z)) return null;
  const gx = (x - t.origin.x) / t.cellSize;
  const gz = (z - t.origin.z) / t.cellSize;
  const i = Math.min(t.width - 2, Math.max(0, Math.floor(gx)));
  const j = Math.min(t.depth - 2, Math.max(0, Math.floor(gz)));
  if (terrainQuadIsHole(t, i, j)) return null;
  const u = Math.min(1, Math.max(0, gx - i));
  const v = Math.min(1, Math.max(0, gz - j));
  const k = j * t.width + i;
  const h00 = t.heights[k] as number;
  const h10 = t.heights[k + 1] as number;
  const h01 = t.heights[k + t.width] as number;
  const h11 = t.heights[k + t.width + 1] as number;
  if (u + v <= 1) {
    return h00 + (h10 - h00) * u + (h01 - h00) * v;
  }
  return h10 * (1 - v) + h01 * (1 - u) + h11 * (u + v - 1);
}

/** Surface palette entry for a terrain quad, or `null` over a hole. */
export function terrainSurfaceAt(t: MapTerrain, x: number, z: number) {
  if (!terrainContains(t, x, z)) return null;
  const i = Math.min(t.width - 2, Math.max(0, Math.floor((x - t.origin.x) / t.cellSize)));
  const j = Math.min(t.depth - 2, Math.max(0, Math.floor((z - t.origin.z) / t.cellSize)));
  if (terrainQuadIsHole(t, i, j)) return null;
  return t.surfaces[t.surfaceIds[j * (t.width - 1) + i] as number] ?? null;
}

/**
 * What is growing at `(x,z)`, or `null`: outside the heightfield, over a hole,
 * or on unlabelled ground (a street, a pad, a pre-label map). The plain
 * composition of `terrainSurfaceAt` and one field read, so the clamping
 * arithmetic exists once.
 */
export function terrainLandAt(t: MapTerrain, x: number, z: number): LandKind | null {
  return terrainSurfaceAt(t, x, z)?.land ?? null;
}
